import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Info, Tag, Sparkles, MessageCircle, ShieldCheck, ArrowUpRight } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { useLang } from "@/context/LanguageContext";
import { PRODUCTS } from "@/i18n/productDetails";
import { CONTACT } from "@/lib/contact";

function SmartImage({ name, alt }) {
  const [src, setSrc] = useState(`/images/${name}.jpg`);
  const [fb, setFb] = useState(false);
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => {
        if (!fb) {
          setSrc(`/images/${name}.svg`);
          setFb(true);
        }
      }}
    />
  );
}

function CustomVisual({ label }) {
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-brand-cloud via-brand-paper to-brand-champagneSoft/50 flex flex-col items-center justify-center text-center p-10 grain">
      <div className="grid place-items-center h-16 w-16 rounded-2xl bg-brand-ink text-brand-champagne">
        <Sparkles className="h-7 w-7" />
      </div>
      <div className="mt-6 font-heading text-xl md:text-2xl font-medium text-brand-ink tracking-tight">
        {label}
      </div>
      <div className="mt-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand-stone">
        QR · NFC · Logo · Farbe
      </div>
    </div>
  );
}

const lget = (obj, lang) => (obj && (obj[lang] ?? obj.de)) || "";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const product = PRODUCTS[slug];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  if (!product) return <Navigate to="/" replace />;

  const isCustom = product.image === "custom";
  const inquiryValue =
    t.bestseller.items.find((i) => i.slug === slug)?.inquiryValue || "Aufsteller";
  const productPrice = t.bestseller.items.find((i) => i.slug === slug)?.price;
  const productBody = t.bestseller.items.find((i) => i.slug === slug)?.body;

  const handlePick = (qty) => {
    try {
      const variant = `${inquiryValue} – ${qty} Stück`;
      window.sessionStorage.setItem("in7s_variant", variant);
      window.dispatchEvent(
        new CustomEvent("in7s:set-variant", { detail: variant })
      );
    } catch (_) {}
  };

  const buildWaMessage = (qty) => {
    const productName =
      lang === "de"
        ? `${lget(product.title, lang)} (${qty} Stück)`
        : `${lget(product.title, lang)} (${qty} pcs)`;
    return lang === "de"
      ? `Hallo in7sekunden, ich interessiere mich für: ${productName}.`
      : `Hi in7sekunden, I'm interested in: ${productName}.`;
  };

  const b = t.bestseller;

  return (
    <div className="min-h-screen bg-white text-brand-ink font-body" data-testid={`product-${slug}-page`}>
      <Header />
      <main className="pt-28 md:pt-36 pb-24 md:pb-32">
        <div className="container-x">
          <Link
            to="/#bestseller"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-brand-stone hover:text-brand-ink transition-colors"
            data-testid="product-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {lang === "de" ? "Zurück zur Übersicht" : "Back to overview"}
          </Link>

          {/* Hero */}
          <div className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-6">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-brand-mist bg-brand-cloud">
                {isCustom ? (
                  <CustomVisual label={lget(product.title, lang)} />
                ) : (
                  <SmartImage name={product.image} alt={lget(product.title, lang)} />
                )}
                <div className="absolute top-4 left-4 chip">
                  <Tag className="h-3 w-3" />
                  {lget(product.tag, lang)}
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <span className="overline">{t.bestseller.overline}</span>
              <h1 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-ink text-balance">
                {lget(product.title, lang)}
              </h1>
              <p className="mt-5 text-lg text-brand-graphite leading-relaxed text-pretty">
                {lget(product.headline, lang)}
              </p>
              <p className="mt-5 text-base text-brand-graphite leading-relaxed text-pretty">
                {lget(product.intro, lang)}
              </p>
              {productBody && (
                <p className="mt-3 text-base text-brand-graphite leading-relaxed text-pretty">
                  {productBody}
                </p>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-5">
                {productPrice && (
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-brand-stone">
                      {b.priceLabel}
                    </div>
                    <div className="font-heading text-3xl font-semibold text-brand-ink">
                      {productPrice}
                    </div>
                  </div>
                )}
                <a
                  href="#pakete"
                  className="btn-primary"
                  data-testid="product-jump-packages"
                >
                  {b.packagesTitle}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Description sections */}
          <div className="mt-20 grid md:grid-cols-3 gap-5 md:gap-6">
            {product.sections.map((s, i) => (
              <article
                key={i}
                className="card-soft p-7 md:p-8"
                data-testid={`product-section-${i}`}
              >
                <div className="font-heading text-base font-medium text-brand-ink tracking-tight">
                  {lget(s.title, lang)}
                </div>
                <p className="mt-3 text-[15px] text-brand-graphite leading-relaxed">
                  {lget(s.body, lang)}
                </p>
              </article>
            ))}
          </div>

          {/* Notice */}
          <div
            id="pakete"
            className="mt-20 rounded-2xl border border-brand-champagne/70 bg-brand-champagneSoft/25 p-5 md:p-6 flex items-start gap-4 max-w-3xl"
            role="note"
          >
            <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-ink text-brand-champagne shrink-0">
              <Info className="h-4 w-4" />
            </span>
            <div>
              <div className="font-heading text-sm md:text-base font-semibold text-brand-ink tracking-tight">
                {b.noticeTitle}
              </div>
              <p className="mt-1 text-sm text-brand-graphite leading-relaxed">
                {b.noticeBody}
              </p>
            </div>
          </div>

          {/* Packages */}
          <section className="mt-12">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink">
                {b.packagesTitle}
              </h2>
            </div>

            <div className="mt-8 grid md:grid-cols-3 gap-5 md:gap-6">
              {b.packages.map((p, i) => (
                <div
                  key={i}
                  className={`relative card-soft card-soft-hover p-7 md:p-8 flex flex-col ${
                    p.badge ? "border-brand-champagne/80" : ""
                  }`}
                  data-testid={`product-package-${p.qty}`}
                >
                  {p.badge && (
                    <span className="absolute -top-3 left-7 inline-flex items-center rounded-full bg-brand-ink text-brand-champagne text-[10px] uppercase tracking-[0.22em] font-semibold px-3 py-1">
                      {p.badge}
                    </span>
                  )}
                  <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-stone">
                    {p.label}
                  </div>
                  <div className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-brand-ink tracking-tight">
                    {p.price}
                  </div>
                  <p className="mt-4 text-sm text-brand-graphite leading-relaxed flex-1">
                    {p.body}
                  </p>
                  <a
                    href="/#contact"
                    onClick={() => handlePick(p.qty)}
                    className="mt-6 btn-primary w-full justify-center"
                    data-testid={`product-package-cta-${p.qty}`}
                  >
                    {b.pickPackage}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                  <a
                    href={CONTACT.whatsappUrl(buildWaMessage(p.qty))}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center justify-center gap-2 w-full text-xs uppercase tracking-[0.18em] font-semibold text-brand-graphite hover:text-brand-ink transition-colors py-2"
                    data-testid={`product-package-whatsapp-${p.qty}`}
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    {lang === "de" ? "Per WhatsApp anfragen" : "Ask via WhatsApp"}
                  </a>
                </div>
              ))}
            </div>

            <p className="mt-6 text-xs text-brand-stone leading-relaxed max-w-3xl">
              {b.packagesNote}
            </p>
          </section>

          {/* Trust list */}
          <ul className="mt-16 grid sm:grid-cols-3 gap-3 md:gap-4 max-w-4xl">
            {[
              lang === "de" ? "Kein Strom, kein WLAN, keine Batterie" : "No power, no Wi-Fi, no battery",
              lang === "de" ? "NFC-Chip + QR-Code in jedem Aufsteller" : "NFC chip + QR code in every stand",
              lang === "de" ? "Versand innerhalb weniger Werktage" : "Shipping within a few business days",
            ].map((x, i) => (
              <li
                key={i}
                className="flex items-start gap-3 rounded-2xl border border-brand-mist bg-brand-cloud p-4"
              >
                <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full bg-brand-ink text-white shrink-0">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <span className="text-sm text-brand-graphite leading-relaxed">{x}</span>
              </li>
            ))}
          </ul>

          {/* Reputation Service add-on */}
          <section
            className="mt-20 rounded-3xl border border-brand-mist bg-brand-ink text-white overflow-hidden grain relative"
            data-testid="product-reputation-addon"
          >
            <div aria-hidden className="absolute inset-0 opacity-30 pointer-events-none">
              <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-brand-champagne/15 blur-3xl" />
            </div>
            <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 p-8 md:p-12 lg:p-14 items-start">
              <div className="lg:col-span-7">
                <span className="overline !text-brand-champagne inline-flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  {lang === "de" ? "Zusatz-Service" : "Add-on service"}
                </span>
                <h2 className="mt-5 font-heading text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-white text-balance">
                  {lang === "de"
                    ? "Bestehende Bewertungen managen – auf Wunsch zubuchbar."
                    : "Manage existing reviews – available as an add-on."}
                </h2>
                <p className="mt-5 text-base md:text-lg text-white/75 leading-relaxed text-pretty max-w-2xl">
                  {lang === "de"
                    ? "Der Aufsteller sammelt neue, ehrliche Bewertungen. Falls bereits negative Google- oder Trustpilot-Einträge vorhanden sind, kümmern wir uns über die Schwesterplattform online-bewertungsmanagement.de zusätzlich um die strukturierte administrative Einreichung – über die offiziellen Plattformwege."
                    : "The stand collects new, honest reviews. If negative Google or Trustpilot entries already exist, our sister platform online-bewertungsmanagement.de additionally takes care of the structured administrative submission via the official platform channels."}
                </p>
                <p className="mt-3 text-xs md:text-sm text-white/50 leading-relaxed max-w-2xl">
                  {lang === "de"
                    ? "Keine Garantie auf Entfernung. Keine juristische Beratung. Die Entscheidung liegt stets bei der jeweiligen Plattform."
                    : "No guarantee of removal. Not legal advice. The decision always lies with the respective platform."}
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/#reputation" className="btn-on-dark" data-testid="product-reputation-internal">
                    {lang === "de" ? "Mehr zum Reputation Service" : "More about Reputation Service"}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <a
                    href="https://www.online-bewertungsmanagement.de"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 text-white px-6 py-3.5 text-sm font-medium tracking-wide transition-all duration-300 hover:border-white hover:-translate-y-0.5"
                    data-testid="product-reputation-external"
                  >
                    online-bewertungsmanagement.de
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <ul className="space-y-3">
                  {(lang === "de"
                    ? [
                        { k: "Aktiv sammeln", v: "Mit dem Aufsteller, am echten Kontaktpunkt." },
                        { k: "Professionell managen", v: "Negative Einträge: strukturierte Einreichung über offizielle Wege." },
                        { k: "Ein System", v: "Beide Bausteine sauber miteinander verzahnt." },
                      ]
                    : [
                        { k: "Actively collect", v: "With the stand, at the real point of contact." },
                        { k: "Professionally manage", v: "Negative entries: structured submission via official channels." },
                        { k: "One system", v: "Both building blocks cleanly connected." },
                      ]
                  ).map((p, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-4"
                    >
                      <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full bg-brand-champagne text-brand-ink shrink-0">
                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      </span>
                      <div>
                        <div className="text-sm font-medium text-white">{p.k}</div>
                        <div className="text-xs text-white/65 leading-relaxed">{p.v}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
